import { opsEn } from "./ops.en";
import { partnerEn } from "./partner.en";

export const en = {
  common: {
    save: "Save",
    saving: "Saving...",
    saved: "Saved",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    remove: "Remove",
    close: "Close",
    back: "Back",
    search: "Search",
    retry: "Try again",
    copy: "Copy",
    copied: "Copied",
    continue: "Continue",
    optional: "optional",
    never: "Never",
    none: "None",
    export: "Export",
    import: "Import",
    name: "Name",
    email: "Email",
    role: "Role",
    status: "Status",
    actions: "Actions",
    loading: "Loading...",
    page: "{{from}} to {{to}} of {{total}}",
    previous: "Previous",
    next: "Next",
    view: "View",
  },
  nav: {
    overview: "Overview",
    contacts: "Contacts",
    lists: "Lists",
    people: "People",
    sync: "Sync",
    activity: "Activity",
    import: "Import",
    settings: "Settings",
    handbook: "Handbook",
    logout: "Sign out",
    theme: "Dark mode",
    language: "Language",
    support: "Support access to {{org}}. Every action is logged in the customer's activity.",
    exitSupport: "End support access",
  },
  roles: { admin: "Administrator", editor: "List editor" },
  notifications: {
    title: "Notifications",
    description: "Which emails Kontaktfix sends to {{email}}.",
    product: {
      title: "Product notifications",
      hint: "For example when someone new cannot receive contacts.",
    },
    lifecycle: {
      title: "Tips for getting started",
      hint: "A few emails with the next useful step, at most one per step.",
    },
    updates: { title: "Product news", hint: "New features, a few times a year." },
    transactional:
      "Emails you need for the service always arrive: invitations, a lost Microsoft 365 connection, expiring certificates, trial, subscription and payment.",
  },
  unsubscribe: {
    title: "Unsubscribe",
    confirm: 'You will no longer receive emails of the type "{{category}}".',
    button: "Unsubscribe",
    done: 'Done. You will no longer receive emails of the type "{{category}}".',
    invalid:
      "This link is not valid. Use the link from the latest email, or change your settings after signing in.",
    manage: "Manage all notifications",
  },
  bugReport: {
    button: "Report a problem",
    title: "Report a problem",
    description: "Tell us what went wrong. We look at every report.",
    tried: "What were you trying to do?",
    happened: "What happened instead?",
    severity: "How much does it affect you?",
    severities: { minor: "Minor", annoying: "Annoying", blocking: "It blocks my work" },
    privacy:
      "The page, browser and recent error messages are added automatically. Your name and email address are not passed on.",
    send: "Send report",
    sent: "Thank you. Your report has been sent.",
  },
  maintenance: {
    title: "Maintenance",
    body: "Kontaktfix is being updated and will be back shortly. This page reloads by itself.",
    delivery: "Contacts already in Outlook stay there.",
  },
  login: {
    title: "Sign in to Kontaktfix",
    subtitle: "Use your Microsoft work account. No extra password needed.",
    microsoft: "Sign in with Microsoft",
    hosted: "Hosted in Switzerland",
    partner: "IT partner?",
    partnerSignIn: "Sign in here",
    partnerSignUp: "Sign up here",
    errors: {
      not_configured: "Microsoft sign-in is not configured on this server yet.",
      state: "The sign-in expired. Please try again.",
      access_denied: "Sign-in was cancelled.",
      default: "Sign-in failed. Please try again.",
    },
  },
  onboarding: {
    createTitle: "Set up your organization",
    createText:
      "You are the first person from your company. Create the organization and you become its administrator. Next you connect Microsoft 365.",
    orgName: "Organization name",
    language: "Language for delivered contacts",
    create: "Create organization",
    noAccessTitle: "No access yet",
    noAccessText:
      "Your company already uses Kontaktfix. Ask an administrator to add you under Settings, Members.",
    signedInAs: "Signed in as {{email}}",
    claimText:
      "Are you a Global Administrator of your Microsoft 365? Then you can take administrator rights yourself, for example when only your IT partner managed Kontaktfix so far.",
    claim: "Take administrator rights",
  },
  home: {
    title: "Overview",
    gettingStarted: "Getting started",
    welcome: "Welcome, {{name}}",
    subtitle: "From signup to shared contacts in Outlook in a few steps.",
    stats: {
      contacts: "Contacts",
      lists: "Lists",
      recipients: "People receiving",
      delivered: "Delivered contacts",
    },
    steps: {
      connect: {
        title: "Connect Microsoft 365",
        text: "Approve Kontaktfix once, or connect your own app registration.",
        action: "Connect",
      },
      lists: {
        title: "Create a contact list",
        text: "For example Suppliers or Emergency numbers.",
        action: "Create list",
      },
      contacts: {
        title: "Add contacts",
        text: "Add them by hand or import Excel, CSV or vCard.",
        action: "Import",
      },
      assign: {
        title: "Assign the list",
        text: "Choose Microsoft 365 groups or people who receive it.",
        action: "Assign",
      },
      sync: {
        title: "First delivery",
        text: "Contacts appear in Outlook, separate from personal contacts.",
        action: "Open sync",
      },
      phone: {
        title: "Contacts on phones",
        text: "Turn on Save contacts in Outlook mobile for caller names.",
        action: "Phone guide",
      },
    },
    done: "Done",
    recent: "Recent activity",
    allActivity: "All activity",
  },
  contacts: {
    title: "Contacts",
    description: "All business contacts of your organization. Contacts in lists are delivered to Outlook.",
    new: "New contact",
    search: "Search name, company, email or phone",
    allLists: "All lists",
    empty: "No contacts yet",
    emptyText: "Create your first contact or import a spreadsheet.",
    noResults: "No contacts match your search.",
    columns: {
      name: "Name",
      company: "Company",
      phone: "Phone",
      email: "Email",
      lists: "Lists",
      updated: "Updated",
    },
    selected: "{{n}} selected",
    pageSelected: "All {{n}} contacts on this page are selected.",
    selectAll: "Select all {{n}} contacts",
    allSelected: "All {{n}} contacts are selected.",
    allSelectedExcept: "{{n}} of {{total}} contacts are selected.",
    clearSelection: "Clear selection",
    addToList: "Add to list",
    removeFromList: "Remove from list",
    deleteSelected: "Delete",
    deleteTitle_one: "Delete {{n}} contact?",
    deleteTitle_other: "Delete {{n}} contacts?",
    deleteText:
      "They are removed from all lists and from every Outlook they were delivered to. This cannot be undone.",
    deleted: "Deleted",
    readOnly: "View only",
    readOnlyHint: "You can edit contacts that are in one of your lists.",
    created: "Contact created",
    updated: "Contact saved",
    addedToList_one: "{{n}} contact added",
    addedToList_other: "{{n}} contacts added",
    removedFromList_one: "{{n}} contact removed",
    removedFromList_other: "{{n}} contacts removed",
    sharedWarning: "Also in {{lists}}. Changes apply there too.",
    managedNote: "Contacts are delivered to Outlook within minutes after saving.",
    tabs: { details: "Details", history: "History" },
    sections: {
      person: "Person",
      company: "Company",
      email: "Email",
      phone: "Phone",
      address: "Address",
      more: "More",
      lists: "Lists",
    },
    fields: {
      title: "Title",
      givenName: "First name",
      middleName: "Middle name",
      surname: "Last name",
      companyName: "Company",
      department: "Department",
      jobTitle: "Job title",
      nickName: "Nickname",
      profession: "Profession",
      officeLocation: "Office location",
      manager: "Manager",
      assistantName: "Assistant",
      imAddress: "Chat address (Teams)",
      email1: "Email",
      email2: "Email 2",
      email3: "Email 3",
      businessPhone1: "Business phone",
      businessPhone2: "Business phone 2",
      mobilePhone: "Mobile",
      homePhone1: "Home phone",
      homePhone2: "Home phone 2",
      street: "Street",
      postalCode: "Postal code",
      city: "City",
      state: "Canton / state",
      country: "Country",
      website: "Website",
      notes: "Notes",
    },
    addressTypes: { businessAddress: "Business", homeAddress: "Home", otherAddress: "Other" },
    addAddress: "Add address",
    moreEmails: "Add email",
    morePhones: "Add phone",
    listsHint: "The contact is delivered to everyone who receives one of these lists.",
    listRequired: "Choose at least one of your lists.",
    photo: "Photo",
    uploadPhoto: "Upload photo",
    removePhoto: "Remove photo",
    photoSaved: "Photo saved",
    validation: {
      invalid_phone: "Not a valid phone number. Use international format, e.g. +41 44 123 45 67.",
      invalid_email: "Not a valid email address.",
      invalid_url: "Not a valid web address.",
      name_or_company_required: "Enter a name or a company.",
    },
    deliveredTo: "Delivered to {{count}} people",
  },
  lists: {
    title: "Lists",
    description:
      "Lists group contacts by purpose. Assign a list to groups or people and they receive its contacts.",
    new: "New list",
    empty: "No lists yet",
    emptyText: "Create a list such as Suppliers, then add contacts and assign it.",
    columns: {
      name: "Name",
      contacts: "Contacts",
      recipients: "Receiving",
      editors: "Editors",
      updated: "Updated",
    },
    form: {
      name: "Name",
      namePlaceholder: "e.g. Suppliers",
      description: "Description",
      color: "Color",
      kind: "Type of list",
      kinds: {
        manual: "Own contacts",
        directory: "Employees from Microsoft 365",
        file: "File import (scheduled)",
      },
      kindHint: {
        manual: "You maintain the contacts in Kontaktfix or import them.",
        directory:
          "Kontaktfix takes the employees from Microsoft 365 and keeps them up to date. They are maintained in Microsoft 365.",
        file: "Kontaktfix fetches a CSV or Excel file regularly, for example a nightly export from your industry or accounting software. It is maintained at the source.",
      },
    },
    file: {
      url: "File address",
      urlHint: "https:// or sftp://, for example a nightly export. Publicly reachable servers only.",
      auth: "Sign-in",
      authTypes: {
        none: "None",
        basic: "Username and password",
        bearer: "Token (Authorization: Bearer)",
        password: "Username and password",
        key: "Username and SSH key",
      },
      username: "Username",
      password: "Password",
      token: "Token",
      privateKey: "Private SSH key",
      secretHint: "Stored encrypted and never shown again.",
      secretStored: "Stored. Leave empty to keep it.",
      schedule: "Fetch",
      schedules: { hourly: "hourly", daily: "daily" },
      deletion: "Contacts no longer in the file",
      deletions: {
        after_days: "remove after {{count}} days",
        immediately: "remove immediately",
        never: "keep",
      },
      deletionDays: "Days until removal",
      deletionHint: "Protects against a broken export: a contact missing only briefly stays on the phones.",
      read: "Read file",
      readHint: "Read the file once to map its columns.",
      readResult: "Read {{rows}} rows, {{columns}} columns.",
      mapping: "Map columns",
      keyColumn: "Unique column",
      keyColumnHint: "Recognizes a contact at the next fetch, for example a customer or address number.",
      keyAuto: "Automatic (email, else name and company)",
      status: "Last fetched: {{when}} · {{schedule}}",
      pending: "not yet",
      stats: "{{rows}} rows: {{created}} new, {{updated}} changed, {{removed}} removed.",
      missing_one: "{{count}} contact is missing from the file and is removed after {{days}} days.",
      missing_other: "{{count}} contacts are missing from the file and are removed after {{days}} days.",
      skipped_one: "{{count}} row skipped (invalid or duplicate).",
      skipped_other: "{{count}} rows skipped (invalid or duplicate).",
      error: "Last attempt failed: {{error}}. The contacts stay unchanged.",
      fetchNow: "Fetch now",
      fetched: "File fetched.",
      reasons: {
        https_or_sftp: "Only https:// and sftp:// are possible",
        host_not_found: "Server not found",
        address_not_allowed: "This server is not publicly reachable",
        connection_failed: "Connection failed",
        timeout: "The server did not answer in time",
        access_denied: "Access denied, check the sign-in",
        http_error: "The server reports an error",
        too_many_redirects: "Too many redirects",
        file_not_found: "File not found",
        file_too_large: "The file is larger than 15 MB",
        host_key_changed: "The server's SSH key changed. Save the address again if this is intended",
        empty_file: "The file is empty",
        too_many_rows: "The file has too many rows",
        unreadable_file: "The file cannot be read",
        no_valid_contacts: "No valid contacts in the file",
        organization_contact_limit: "Your organization's contact limit would be exceeded",
        recipient_limit: "Some people would receive too many contacts",
        credentials_unavailable: "Credentials cannot be stored on this server",
      },
    },
    directory: {
      scope: "Who is on the list?",
      scopeAll: "Everybody in Microsoft 365",
      scopeGroups: "Members of certain groups",
      groups: "Groups",
      addGroup: "Add group",
      filters: "Only people with",
      filtersHint: "Separate several values with commas. Leave empty for everybody.",
      filter: {
        companyName: "Company",
        department: "Department",
        officeLocation: "Office location",
        country: "Country",
        mailDomain: "Mail domain",
      },
      fieldsTitle: "Which details go to the phones?",
      fields: {
        jobTitle: "Job title",
        department: "Department",
        companyName: "Company",
        officeLocation: "Office location",
        email: "Email",
        businessPhones: "Business phone",
        mobilePhone: "Mobile",
        businessAddress: "Business address",
        photo: "Profile photo",
      },
      mobileHint: "Often a private number. Only switch on if everyone agrees.",
      include: "Also include",
      includeHint: "For example a shared mailbox like Support or Reception.",
      exclude: "Never include",
      excludeHint: "For example the management or people with a private number in Microsoft 365.",
      addPerson: "Add person or mailbox",
      status: "This list is filled from Microsoft 365 and refreshed every 10 minutes. Last: {{when}}.",
      pending: "shortly",
      error: "Last refresh failed: {{error}}",
      excludedCount_one: "{{count}} person not on the list",
      excludedCount_other: "{{count}} people not on the list",
      reasons: {
        guest: "Guest",
        disabled: "Account disabled",
        no_mail: "No email address",
        hidden: "Hidden from the address book in Microsoft 365",
        filtered: "Does not match the filter",
        excluded: "Excluded by you",
      },
    },
    created: "List created",
    updated: "List saved",
    deleteTitle: "Delete list {{name}}?",
    deleteText:
      "Contacts stay in your organization but are removed from the Outlook of everyone who received them only through this list.",
    deleted: "List deleted",
    tabs: { contacts: "Contacts", assignments: "Assigned to", editors: "Editors", history: "History" },
    addContacts: "Add contacts",
    addContactsTitle: "Add contacts to {{name}}",
    assignments: {
      title: "Who receives this list",
      text: "Group assignments follow the group automatically: people who join receive the list, people who leave lose it.",
      add: "Assign",
      search: "Search groups and people in Microsoft 365",
      group: "Group",
      user: "Person",
      members_one: "{{count}} member",
      members_other: "{{count}} members",
      missing: "No longer in Microsoft 365",
      empty: "Not assigned yet",
      emptyText: "Nobody receives this list. Assign it to a group like All employees.",
      added: "Assigned. Delivery starts within a minute.",
      removed: "Assignment removed",
      noResults: "No groups or people found.",
    },
    impact: {
      seats_one: "This adds {{people}} new person, {{amount}} more per month.",
      seats_other: "This adds {{people}} new people, {{amount}} more per month.",
      title: "Assign the list to {{name}}",
      summary_one: "{{people}} person will then receive up to {{contacts}} contacts.",
      summary_other: "{{people}} people will then receive up to {{contacts}} contacts each.",
      duration:
        "The first delivery takes about {{from}} to {{to}}. Microsoft limits how fast contacts may be written to a mailbox. After that only changes are transferred, which takes seconds.",
      phone:
        "On the phone the contacts appear once Outlook has synced them. With many contacts this can take a few more minutes after the delivery.",
      warning: "That is a lot of contacts for a phone. Check whether everyone really needs every list.",
      overLimit:
        "That is more than the limit of {{limit}} contacts per person. Make the lists smaller or assign them to fewer people. Contact us for a higher limit.",
      minutes_one: "{{count}} minute",
      minutes_other: "{{count}} minutes",
      hours_one: "{{count}} hour",
      hours_other: "{{count}} hours",
    },
    editors: {
      title: "List editors",
      text: "Editors maintain the contacts of this list without being administrators.",
      none: "No editors. Only administrators can change this list.",
      manage: "Choose editors",
      saved: "Editors saved",
      noMembers: "Add colleagues under Settings, Members first.",
    },
    recipientsCount_one: "{{count}} person",
    recipientsCount_other: "{{count}} people",
    readOnly: "You can view this list. Only its editors and administrators can change it.",
  },
  people: {
    title: "People",
    description: "Everyone who receives contacts, and why.",
    empty: "Nobody receives contacts yet",
    emptyText: "Assign a list to a group or person to start delivery.",
    search: "Search people",
    columns: {
      person: "Person",
      status: "Status",
      lists: "Lists",
      contacts: "Contacts",
      lastSync: "Last delivery",
    },
    receives: "Receives",
    direct: "Assigned directly",
    viaGroup: "Through group {{group}}",
  },
  carddav: {
    title: "Phone without Outlook (CardDAV)",
    description:
      "For phones without Outlook: the phone's own Contacts app reads this person's contacts read-only over CardDAV.",
    none: "No phone access yet.",
    active: "Access {{username}} since {{date}}, last used {{used}}.",
    create: "Create access",
    renew: "New password",
    remove: "Remove access",
    removed: "Phone access removed",
    removeTitle: "Remove the phone access of {{name}}?",
    removeText: "The contacts disappear from the phone at its next sync. Outlook is not affected.",
    createdText:
      "Hand these details to the person or your MDM. The password is shown only now; it is not stored in readable form.",
    server: "Server",
    username: "Username",
    password: "Password",
    profile: "Download iPhone profile",
    setup:
      "iPhone and iPad: open the profile and install it under Settings > General > VPN & Device Management, or push it with your MDM. Android: add a CardDAV account in DAVx5 with the server, username and password.",
    twice:
      "If Outlook on this phone already saves the contacts, use only one of the two, otherwise contacts appear twice. Edits on the phone are not kept.",
  },
  status: {
    pending: "Pending",
    syncing: "Delivering",
    ok: "Up to date",
    error: "Error",
    unreachable: "Not reachable",
    removing: "Removing",
  },
  reasons: {
    no_mailbox: "No Exchange Online mailbox",
    out_of_scope: "Outside the allowed mailbox scope",
    consent_missing: "Microsoft 365 connection missing",
    throttled: "Microsoft is slowing down requests, retrying",
    guest: "Guest account, cannot receive contacts",
    cancelled: 'Delivery stopped, continues with "Sync now"',
    unknown: "Unexpected error",
  },
  sync: {
    title: "Sync",
    description: "Delivery status across all mailboxes. Changes are delivered automatically within minutes.",
    syncNow: "Sync now",
    syncRequested: "Sync started",
    lastSuccess: "Last delivery",
    recipients: "People",
    delivered: "Contacts in mailboxes",
    problems: "Need attention",
    problemsEmpty: "Everyone is reachable.",
    upToDate: "Up to date",
    attention_one: "{{count}} person needs attention",
    attention_other: "{{count}} people need attention",
    remaining_one: "about {{count}} minute left",
    remaining_other: "about {{count}} minutes left",
    inProgress: "Delivering...",
    runs: "Recent runs",
    noRuns: "No runs yet.",
    columns: {
      started: "Started",
      trigger: "Trigger",
      progress: "Progress",
      changes: "Changes",
      errors: "Errors",
    },
    triggers: {
      change: "Change",
      manual: "Manual",
      schedule: "Nightly check",
      directory: "Group membership",
      setup: "Setup",
      disconnect: "Disconnect",
    },
    run: {
      title: "Run",
      showChanges: "Show changed contacts",
      status: { running: "Running", done: "Finished", failed: "Failed", cancelled: "Stopped" },
      stop: "Stop",
      stopTitle: "Stop this run?",
      stopText:
        'Waiting mailboxes are skipped, running ones stop after their current block of at most 20 contacts. What was delivered stays. The stopped mailboxes are delivered again with "Sync now", when contacts change, or in the nightly check.',
      duration: { s: "{{s}} s", ms: "{{m}} min {{s}} s", hm: "{{h}} h {{m}} min" },
      mailboxesDone: "{{done}}/{{total}} mailboxes",
      writes: "{{done}} of {{total}} writes",
      mailboxCount: {
        running_one: "{{count}} running",
        running_other: "{{count}} running",
        queued_one: "{{count}} waiting",
        queued_other: "{{count}} waiting",
        done_one: "{{count}} done",
        done_other: "{{count}} done",
        failed_one: "{{count}} failed",
        failed_other: "{{count}} failed",
        superseded_one: "{{count}} taken over by a later run",
        superseded_other: "{{count}} taken over by a later run",
        cancelled_one: "{{count}} stopped",
        cancelled_other: "{{count}} stopped",
      },
      mailboxStatus: {
        queued: "Waiting",
        running: "Running",
        done: "Done",
        failed: "Failed",
        superseded: "Taken over",
        cancelled: "Stopped",
      },
      created: "New",
      updated: "Updated",
      deleted: "Removed",
      errors: "Errors",
      requests: "Requests to Microsoft",
      photos_one: "{{count}} photo",
      photos_other: "{{count}} photos",
      throttled: "{{duration}} throttled",
      throttledShort: ", {{duration}} throttled by Microsoft",
      tabs: { mailboxes: "Mailboxes", changes: "Changes", log: "Log" },
      columns: {
        person: "Person",
        status: "Status",
        progress: "Progress",
        requests: "Requests",
        duration: "Duration",
        contact: "Contact",
        change: "Change",
        reason: "Reason",
        mailboxes: "Mailboxes",
      },
      ops: { all: "All", create: "New", update: "Updated", delete: "Removed" },
      reasons: {
        changed: "Details changed",
        edited_in_outlook: "Changed in Outlook, reset",
        not_desired: "No longer in the person's lists",
        duplicate: "Duplicate in the mailbox",
        orphan: "Unknown Kontaktfix contact",
        moved: "In the old location (delivery mode changed)",
      },
      inMailboxes_one: "in {{count}} mailbox",
      inMailboxes_other: "in {{count}} mailboxes",
      failedIn_one: "failed in {{count}}",
      failedIn_other: "failed in {{count}}",
      errorsOnly: "Only with errors",
      searchContact: "Search contact",
      noChanges: "No changes.",
      unknownContact: "Unknown contact",
      shown: "{{shown}} of {{total}} shown",
      more: "Show more",
      levels: { all: "Everything", warn: "Warnings and errors", error: "Errors only" },
      live: "Live",
      noDetails: "Only the totals exist for this run: it ran before detailed tracking was introduced.",
      noEvents: "No entries yet.",
      events: {
        run_started:
          "Run started ({{trigger}}): {{mailboxes}} mailboxes to check, {{people}} people receive contacts.",
        mailbox_started:
          "{{name}}: should have {{contacts}} contacts, {{existing}} are in the mailbox. Plan: {{create}} new, {{update}} update, {{delete}} remove.",
        mailbox_throttled:
          "{{name}}: Microsoft is throttling, waited {{duration}}. Kontaktfix continues automatically.",
        contact_failed: '{{name}}: "{{label}}" ({{opText}}) failed: {{error}}',
        mailbox_done:
          "{{name}}: done in {{duration}}. {{created}} new, {{updated}} updated, {{deleted}} removed, {{photos}} photos, {{errors}} errors, {{calls}} requests{{throttled}}.",
        mailbox_failed: "{{name}}: stopped after {{duration}}. {{reason}}: {{detail}}",
        mailbox_superseded: "{{name}}: delivered by a later run.",
        mailbox_interrupted:
          "{{name}}: interrupted (no sign of life for 20 minutes). The next run continues.",
        run_cancelled: "Run stopped, {{skipped}} waiting mailboxes skipped.",
        mailbox_cancelled:
          "{{name}}: stopped after {{duration}}. Up to then {{created}} new, {{updated}} changed, {{deleted}} removed.",
        run_finished:
          "Run finished after {{duration}}: {{created}} new, {{updated}} updated, {{deleted}} removed, {{errors}} errors, {{failed}} mailboxes failed.",
      },
    },
    connectionMissing: "Microsoft 365 is not connected, so nothing can be delivered.",
    connect: "Connect now",
  },
  activity: {
    title: "Activity",
    description: "Who changed what, and when.",
    empty: "No activity yet.",
    system: "Kontaktfix",
    filters: { all: "All", contact: "Contacts", list: "Lists", member: "Members", settings: "Settings" },
    actions: {
      file_imported: "fetched the file: {{created}} new, {{updated}} changed, {{removed}} removed in",
      file_fetched: "fetched the file for",
      requested: "requested:",
      created: "created",
      updated: "updated",
      deleted: "deleted",
      deleted_many: "deleted {{count}} contacts",
      photo_changed: "changed the photo of",
      photo_removed: "removed the photo of",
      contacts_added: "added {{count}} contacts to",
      contacts_removed: "removed {{count}} contacts from",
      assigned: "assigned {{target}} to",
      unassigned: "removed {{target}} from",
      editors_changed: "changed the editors of",
      added: "added",
      removed: "removed",
      role_changed: "changed the role of",
      organization_created: "created the organization",
      connected: "connected Microsoft 365",
      consent_failed: "tried to connect Microsoft 365",
      disconnected: "disconnected Microsoft 365",
      disconnect_started: "started disconnecting Microsoft 365",
      disconnect_cancelled: "kept the Microsoft 365 connection",
      imported: "imported {{created}} new and {{updated}} updated contacts",
      sync_requested: "started a sync",
      sync_cancelled: "stopped a sync",
      support_access_started: "started support access",
      support_access_ended: "ended support access",
      organization_suspended: "suspended the organization",
      organization_resumed: "resumed the organization",
      checkout_started: "opened the subscription checkout",
      subscription_changed: "changed the subscription status",
      trial_extended: "extended the free trial",
      linked: "linked the IT partner",
      link_requested: "asked for access as the IT partner",
      approved: "approved the IT partner",
      declined: "declined the request of the IT partner",
      unlinked: "removed the IT partner",
      admin_claimed: "took administrator rights as Global Administrator",
      billed_to_partner: "set billing to the IT partner",
      billed_to_customer: "set billing back to the organization",
      carddav_created: "created phone access (CardDAV) for",
      carddav_removed: "removed phone access (CardDAV) for",
      api_key_created: "created the API key",
      api_key_revoked: "revoked the API key",
      webhook_created: "added the webhook",
      webhook_removed: "removed the webhook",
    },
    entity: {
      contact: "contact",
      list: "list",
      member: "member",
      settings: "settings",
    },
    showChanges: "Show changes",
    empty_value: "empty",
  },
  import: {
    title: "Import contacts",
    description: "Excel, CSV or vCard. You see a preview with duplicate warnings before anything is saved.",
    steps: { upload: "Upload", map: "Columns", review: "Review" },
    drop: "Drop a file here or click to choose",
    formats: "Excel (.xlsx), CSV or vCard (.vcf), up to 25,000 contacts",
    parsing: "Reading file...",
    mapTitle: "Match the columns",
    mapText: "We guessed the fields from the column names. Check and adjust.",
    column: "Column in file",
    sample: "Example",
    field: "Kontaktfix field",
    ignore: "Ignore",
    reviewTitle: "Review {{count}} contacts",
    valid: "{{count}} ready",
    invalid: "{{count}} with errors (skipped)",
    duplicates: "{{count}} possible duplicates",
    duplicateOf: "Similar to {{name}} ({{reason}})",
    reasons: { email: "same email", phone: "same phone", name: "same name" },
    inFile: "Duplicate within the file",
    actionCreate: "Create new",
    actionUpdate: "Update existing",
    actionSkip: "Skip",
    targetLists: "Add to lists",
    targetListsHint: "Imported contacts go into these lists.",
    submit_one: "Import {{count}} contact",
    submit_other: "Import {{count}} contacts",
    done: "{{created}} created, {{updated}} updated",
    saving: "{{done}} of {{total}} saved...",
    resume: "Continue import ({{done}} of {{total}} saved)",
    partial: "{{done}} contacts are already saved. Click again to import the rest.",
    fullName: "Full name",
    fields: {
      businessStreet: "Business street",
      businessPostalCode: "Business postal code",
      businessCity: "Business city",
      businessState: "Business canton / state",
      businessCountry: "Business country",
      homeStreet: "Home street",
      homePostalCode: "Home postal code",
      homeCity: "Home city",
      homeCountry: "Home country",
    },
  },
  apiAccess: {
    title: "API",
    docs: "API documentation",
    adminOnly: "Administrators create API keys and webhooks for the Kontaktfix REST API.",
    scope: { read: "Read", write: "Read and write" },
    scopeHint: {
      read: "Read contacts, lists and the delivery status.",
      write: "Also create, change and delete contacts and lists.",
    },
    keys: {
      title: "API keys",
      description:
        "Let your own scripts and systems maintain contacts through the REST API. Every change made with a key appears in the activity log with the key's name.",
      create: "Create API key",
      empty: "No API keys yet.",
      name: "Name",
      nameHint: "What the key is for, e.g. ERP sync.",
      access: "Access",
      lists: "Lists",
      allLists: "All lists",
      someLists: "Selected lists only",
      someListsHint:
        "The key works like a list editor: it sees and changes only contacts in these lists and cannot delete contacts or manage lists.",
      expiry: "Expires",
      days_one: "After {{count}} day",
      days_other: "After {{count}} days",
      noExpiry: "Never",
      expired: "Expired",
      meta: "created {{created}} by {{by}} · last used: {{used}} · expires: {{expires}}",
      createdTitle: "Copy your API key now",
      createdText:
        "This is the only time the key is shown. Store it in a password manager or your system's secret store. Kontaktfix only keeps a fingerprint of it.",
      revoke: "Revoke",
      revokeTitle: "Revoke the key {{name}}?",
      revokeText: "Systems using this key stop working at once. This cannot be undone.",
      revoked: "API key revoked",
    },
    webhooks: {
      title: "Webhooks",
      description:
        "Kontaktfix calls your HTTPS address when something changes, signed so you can check it came from us. Usually within a minute.",
      add: "Add webhook",
      empty: "No webhooks yet.",
      url: "Address",
      urlHint: "HTTPS only. A token in the address is allowed.",
      httpsRequired: "The address must start with https://",
      eventsLabel: "Events",
      events_one: "{{count}} event",
      events_other: "{{count}} events",
      last: "last delivery {{when}}",
      never: "nothing delivered yet",
      pending_one: "{{count}} waiting for retry",
      pending_other: "{{count}} waiting for retry",
      ok: "OK",
      failed: "Failed",
      test: "Send test event",
      testQueued: "Test event queued. It arrives within a minute.",
      createdTitle: "Copy the signing secret now",
      createdText:
        "Your server uses this secret to check the webhook-signature header. It is only shown now.",
      remove: "Remove",
      removeTitle: "Remove the webhook?",
      removeText: "{{url}} receives no more events.",
      removed: "Webhook removed",
    },
    eventText: {
      contact_created: "A contact was created.",
      contact_updated: "A contact or its photo was changed.",
      contact_deleted: "A contact was deleted.",
      list_created: "A list was created.",
      list_updated: "Name, description or color of a list changed.",
      list_deleted: "A list was deleted.",
      list_contacts_changed: "Contacts were added to or removed from a list.",
      sync_completed: "A delivery run finished.",
      delivery_failed: "Delivery to a mailbox failed.",
      recipient_unreachable: "A person cannot be reached, e.g. no mailbox.",
    },
  },
  integrations: {
    available: "Available",
    availableText: "Included in the price, also during the trial.",
    createList: "Create list",
    built: {
      directory: {
        name: "Microsoft 365 directory",
        text: "Employees as contacts, always up to date. Changes appear within about a minute.",
      },
      file: {
        name: "File import (CSV, Excel)",
        text: "Fetches a file hourly or daily over HTTPS or SFTP. Works with almost any software that can export addresses.",
      },
    },
    onRequest: "On request",
    onRequestText:
      "We build direct connections as soon as customers need them. Ask us and we will get back to you. Until then, the file import helps.",
    onRequestBadge: "On request",
    request: "Request",
    requested: "Requested",
    requestSent: "Thank you, we will get back to you.",
    connectors: {
      bexio: { name: "bexio", text: "Contacts and contact groups from bexio." },
      abacus: { name: "Abacus", text: "Addresses from Abacus, for example customers and suppliers." },
      sage: { name: "Sage 50 / Sage 200", text: "Addresses from Swiss Sage accounting." },
      hubspot: { name: "HubSpot", text: "Contacts and companies from HubSpot CRM, filtered by lists." },
      pipedrive: { name: "Pipedrive", text: "People and organizations from Pipedrive." },
      dynamics: {
        name: "Microsoft Dynamics 365",
        text: "Contacts from Dynamics 365 Sales or Business Central.",
      },
      zapier: { name: "Zapier and Make", text: "Contacts from more than 6,000 apps through automation." },
    },
  },
  settings: {
    title: "Settings",
    tabs: {
      general: "General",
      members: "Members",
      microsoft: "Microsoft 365",
      delivery: "Delivery",
      integrations: "Integrations",
      api: "API",
      billing: "Subscription",
      partner: "Partner",
    },
    partner: {
      title: "IT partners",
      text: "An IT partner can manage Kontaktfix for you with its own accounts. Every action of the partner appears in the history with the partner's name. You can revoke the access at any time.",
      code: "Partner code",
      codeHint: "8 characters, from your IT partner.",
      link: "Give access",
      linked: "The partner now has access.",
      empty: "No IT partner has access.",
      pending: "Asks for access",
      active: "Has access",
      since: "since {{date}}",
      requested: "asked on {{date}}",
      approve: "Approve",
      decline: "Decline",
      revoke: "Revoke access",
      revokeTitle: "Revoke access for {{name}}?",
      revokeText:
        "The partner's technicians lose access at once. Your contacts and settings stay as they are.",
      approved: "Access approved.",
      revoked: "Access revoked.",
      declined: "Request declined.",
      partnerOnly: "Only your own administrators link or approve partners.",
    },
    general: {
      orgName: "Organization name",
      language: "Language",
      languageHint: "Used for delivered folder names and notes.",
      tenant: "Microsoft 365 tenant ID",
    },
    members: {
      title: "Members",
      text: "Administrators manage everything. List editors see and maintain only their own lists and the contacts in them. Members sign in with their Microsoft work account.",
      add: "Add member",
      addTitle: "Add a colleague",
      search: "Search your Microsoft 365 directory",
      lastLogin: "Last sign-in",
      notYet: "Not signed in yet",
      editorOf: "Edits {{lists}}",
      removeTitle: "Remove {{name}}?",
      removeText: "They lose access to Kontaktfix. Contacts they received in Outlook are not affected.",
      added: "Member added",
      you: "You",
    },
    microsoft: {
      title: "Microsoft 365 connection",
      status: { granted: "Connected", none: "Not connected", error: "Connection problem" },
      checked: "Last checked {{time}}",
      connect: "Connect Microsoft 365",
      reconnect: "Approve again",
      test: "Check connection",
      connectText:
        "Kontaktfix writes contacts into mailboxes and reads users and groups. It never reads mail, calendars or personal contacts. Choose how a Microsoft 365 administrator connects it.",
      method: { consent: "Approve the Kontaktfix app", own_app: "Your own app registration" },
      methodText: {
        consent:
          "One click. A Global Administrator approves Kontaktfix on Microsoft's consent page; Microsoft then creates its service principal in your tenant.",
        own_app:
          "For IT teams that want to own the service principal. You create the app in Microsoft Entra with a certificate or client secret and can revoke it at any time. About 5 minutes with our script.",
      },
      setUpOwnApp: "Set up",
      editOwnApp: "Change app registration",
      clientId: "Application (client) ID",
      certificateUntil: "Certificate valid until {{date}}",
      secretStored: "Client secret {{hint}} stored encrypted",
      disconnect: "Disconnect",
      disconnectTitle: "Disconnect Microsoft 365?",
      disconnectText:
        "Kontaktfix first removes every contact it delivered from all mailboxes, then forgets the connection. Personal contacts are not touched. Lists and contacts stay in Kontaktfix. To fully revoke access, also delete the Kontaktfix app in Microsoft Entra afterwards.",
      cleanup: {
        title_one: "Disconnecting: removing contacts from {{count}} mailbox",
        title_other: "Disconnecting: removing contacts from {{count}} mailboxes",
        text: "Kontaktfix disconnects automatically once every mailbox is clean, usually within minutes.",
        blocked:
          "The connection to Microsoft 365 does not work, so nothing can be removed. Approve Kontaktfix again (or fix your app registration) to finish, or disconnect without removing contacts.",
        keep: "Keep the connection",
        force: "Disconnect without removing",
        forceTitle: "Disconnect without removing contacts?",
        forceText:
          "Contacts already delivered stay in the mailboxes and are no longer updated. Employees have to delete them by hand.",
      },
      permissions: "Permissions requested",
      perms: {
        contacts: "Contacts.ReadWrite: write delivered contacts into mailboxes",
        users: "User.Read.All: pick people and check their mailbox",
        groups: "GroupMember.Read.All: pick groups and follow their members",
      },
      groupsPending: {
        consent:
          'Changes to people appear within about a minute. For groups, Kontaktfix needs a new permission (Group.Read.All): click "Approve again" and confirm. Until then Kontaktfix checks groups every 10 minutes.',
        own_app:
          "Changes to people appear within about a minute. For group changes to arrive right away too, add the application permission Group.Read.All to your app registration and grant consent. Until then Kontaktfix checks groups every 10 minutes.",
      },
      consentOk: "Microsoft 365 connected. The first delivery starts now.",
      consentError: "The connection did not work: {{reason}}",
      notConfigured: "This server has no Microsoft app registration yet. Ask the Kontaktfix operator.",
      hardenedTitle: "Restrict access to selected mailboxes (optional)",
      hardenedText:
        "By default Microsoft lets an approved app reach all mailboxes. To limit Kontaktfix to members of one group, an Exchange administrator runs this script once. Changes can take up to 2 hours to apply.",
    },
    delivery: {
      title: "Where contacts go in Outlook",
      folder: "Dedicated folder",
      folderText:
        "Kontaktfix creates its own contact folder, apart from personal contacts. Classic Outlook shows it as a folder; the new Outlook and Outlook on the web show it as a category with the folder name.",
      main: "Main contacts folder",
      mainText:
        "The contacts go into the normal Contacts folder, next to personal contacts. Every Outlook and every phone shows them, so caller names work. To tell them apart, turn on categories below.",
      folderName: "Folder name",
      folderNameHint:
        "Folder name in classic Outlook, category name in the new Outlook and Outlook on the web.",
      noteText: "Note added to every contact",
      noteHint: "Tells employees that changes made in Outlook are overwritten.",
      categories: "Show the list as an Outlook category",
      categoriesHint:
        "Each contact is labelled with the names of its lists, so employees can filter by list in Outlook. Personal contacts are not labelled.",
      saved: "Delivery settings saved. Mailboxes are updated within minutes.",
    },
  },
  phone: {
    title: "Contacts on phones",
    description:
      "With one setting in the Outlook app, delivered contacts appear in the phone's address book and incoming calls show the caller's name.",
    iosTitle: "iPhone",
    androidTitle: "Android",
    ios: [
      "Open the Outlook app and tap your profile picture.",
      "Tap Settings, then your work account.",
      "Turn on Save contacts and allow access to contacts.",
    ],
    android: [
      "Open the Outlook app and tap your profile picture.",
      "Tap Settings, then your work account.",
      "Turn on Sync contacts and allow access to contacts.",
    ],
    itTitle: "For IT: turn it on for everyone",
    itText:
      "With Microsoft Intune, create an app configuration policy for Outlook and enable contact sync. Company policies can also block it; then caller names are not shown.",
    noteTitle: "Good to know",
    notes: [
      "Changes made in Kontaktfix reach phones after Outlook syncs, usually within minutes.",
      "Edits made on the phone are not sent back and are overwritten at the next update.",
      "If contacts appear in Outlook but not on the phone, the setting is off or blocked by a policy.",
    ],
  },

  ownApp: {
    back: "Microsoft 365",
    title: "Connect your own app registration",
    description:
      "You create an app registration in your Microsoft Entra tenant and stay in full control of its service principal and credentials. Kontaktfix only gets the three permissions below.",
    status: { connected: "Connected", notConnected: "Not connected" },
    recommended: "Recommended",
    unavailable: "Own app registrations are not enabled on this server. Ask the Kontaktfix operator.",
    step1: {
      title: "Choose how the app signs in",
      text: "Kontaktfix uses this credential to get access tokens for your tenant.",
    },
    kind: {
      certificate: "Certificate",
      certificateText:
        "Kontaktfix created a certificate for your organization. You upload only the public part; nothing secret is copied. Valid until {{date}}.",
      secret: "Client secret",
      secretText:
        "You create a secret in Entra and paste it here. Kontaktfix stores it encrypted. Secrets expire after at most 2 years.",
    },
    step2: {
      title: "Create the app registration",
      text: "Run our script, or click through Microsoft Entra yourself. Both give the same result.",
    },
    way: { script: "PowerShell script", manual: "Manually in Microsoft Entra" },
    script: {
      text: "Run it in PowerShell 7 as Global Administrator. It creates the app, adds the permissions, grants admin consent and uploads the certificate.",
      download: "Download",
      outputCert: "At the end the script prints the Application (client) ID. Paste it in step 3.",
      outputSecret:
        "At the end the script prints the Application (client) ID and the client secret. Paste both in step 3.",
    },
    manual: {
      register: "In Microsoft Entra, open App registrations and choose New registration.",
      name: "Name",
      accountTypes:
        "Supported account types: Accounts in this organizational directory only. No redirect URI.",
      openEntra: "Open App registrations",
      permissions:
        "Open API permissions, Add a permission, Microsoft Graph, Application permissions, and add these three:",
      permission: "Permission",
      type: "Type",
      purpose: "Used for",
      application: "Application",
      grant:
        "Still under API permissions, choose Grant admin consent for your organization. All three show Granted.",
      uploadCert: "Open Certificates & secrets, Certificates, Upload certificate, and upload this file:",
      createSecret:
        "Open Certificates & secrets, Client secrets, New client secret (24 months). Copy the Value right away (not the Secret ID); Entra shows it only once.",
      copyId: "On the Overview page, copy the Application (client) ID.",
    },
    purpose: {
      contacts: "Write delivered contacts into mailboxes",
      users: "Pick people and check their mailbox",
      groups: "Pick groups and follow their members",
    },
    downloadCert: "Download kontaktfix.cer",
    thumbprint: "SHA-256 thumbprint",
    step3: {
      title: "Verify and connect",
      text: "Kontaktfix signs in with your app and checks every permission. Nothing is saved until all checks pass.",
    },
    tenantId: "Directory (tenant) ID",
    tenantIdHint: "The app must be registered in this tenant.",
    clientId: "Application (client) ID",
    secret: "Client secret value",
    secretHint: "The Value column in Entra, not the Secret ID.",
    secretKeep: "Leave empty to keep the stored secret {{hint}}.",
    verify: "Verify and connect",
    verifyAgain: "Verify again",
    retrying: "Microsoft can take a few minutes to apply new permissions. Checking again in {{seconds}} s.",
    checkNow: "Check now",
    connected: "Microsoft 365 connected. The first delivery starts now.",
    done: "Done",
    check: {
      title: "Check",
      status: "Status",
      running: "Checking",
      waiting: "Waiting",
      ok: "Verified",
      failed: "Failed",
      credential: { certificate: "Sign-in with the certificate", secret: "Sign-in with the client secret" },
    },
    reason: {
      app_not_found:
        "No app with this client ID exists in your tenant. Check the ID on the app's Overview page.",
      tenant_not_found: "The tenant was not found.",
      invalid_secret: "The client secret is wrong. Paste the Value, not the Secret ID.",
      secret_expired: "The client secret has expired. Create a new one in Entra.",
      certificate_not_uploaded:
        "The app does not accept the Kontaktfix certificate. Upload kontaktfix.cer under Certificates & secrets.",
      kontaktfix_app:
        "This is the Kontaktfix app itself. Enter the client ID of the app you registered, or use one-click approval instead.",
      not_granted: "Not granted. Add it as an application permission and grant admin consent.",
      not_effective:
        "Granted, but Microsoft does not apply it yet. This usually resolves within a few minutes.",
      unknown: "Microsoft returned an unexpected error.",
    },
  },
  billing: {
    title: "Subscription",
    partnerPays: "{{partner}} pays for Kontaktfix as your IT partner. You do not need your own subscription.",
    partnerEnds:
      "{{partner}} pays until {{date}}. After that you need your own subscription; if you subscribe now, the first payment is due on {{date}}.",
    partnerMissing:
      "{{partner}} pays for Kontaktfix as your IT partner, but has not set up payment yet. Until then, contacts are not delivered. Please contact your partner.",
    ownEnding: "Your own subscription ends on {{date}}. After that your IT partner pays.",
    subscribe: "Subscribe",
    portal: "Invoices and payment",
    subscribed: "Thank you. Your subscription is active.",
    waitingForStripe: "Confirming the payment with Stripe…",
    readOnly: "Your free trial has ended. Contacts are read-only until an administrator subscribes.",
    sidebarTrial_one: "Free trial: {{count}} day left",
    sidebarTrial_other: "Free trial: {{count}} days left",
    states: {
      disabled: "Not configured",
      trial_pending: "Trial not started",
      trial: "Free trial",
      active: "Active",
      past_due: "Payment failed",
      expired: "Trial ended",
      canceled: "Ended",
    },
    status: {
      disabled: "Billing is not configured on this server. Everything works without limits.",
      trial_pending:
        "Your free {{days}}-day trial starts as soon as the first contacts reach a mailbox. No credit card needed.",
      trial_one: "Free trial until {{date}} ({{count}} day left). No credit card needed.",
      trial_other: "Free trial until {{date}} ({{count}} days left). No credit card needed.",
      active: "Your subscription is active. Next invoice on {{date}}.",
      activeEnding: "Your subscription is canceled and ends on {{date}}.",
      past_due:
        "The last payment failed. Stripe tries again automatically. Please check your payment method.",
      expired:
        "The free trial ended on {{date}}. Contacts are no longer delivered and cannot be created or edited until you subscribe.",
      canceled:
        "Your subscription has ended. Contacts are no longer delivered and cannot be created or edited until you subscribe again.",
    },
    users: "Users receiving contacts",
    usersHint: "Counted daily",
    pricePerUser: "Price per user",
    perUserMonth: "per user and month",
    estimate: "Monthly total",
    perMonth: "at the current number of users",
    volume_one: "From {{count}} user we offer volume pricing. Write to",
    volume_other: "From {{count}} users we offer volume pricing. Write to",
    howTitle: "How billing works",
    howUsers:
      "You pay for each person in Microsoft 365 who receives contacts, directly or through a group (nested groups included). Each person counts once, however many lists they receive. Guests and people without a mailbox do not count.",
    howBilling:
      "The subscription is billed monthly. The number of users is updated every day; when it changes, the difference is charged or credited pro rata on the next invoice. You can cancel at any time in Invoices and payment.",
    howTrial:
      "The {{days}}-day free trial starts with the first delivery. If you subscribe during the trial, the first payment is due when the trial ends. When it ends without a subscription, delivery pauses and contacts become read-only. Contacts already in Outlook stay there.",
    banner: {
      trialEnding_one: "Your free trial ends in {{count}} day.",
      trialEnding_other: "Your free trial ends in {{count}} days.",
      expired: "Your free trial has ended. Contacts are no longer delivered and cannot be created or edited.",
      canceled:
        "Your subscription has ended. Contacts are no longer delivered and cannot be created or edited.",
      pastDue: "A payment failed. Please update your payment method.",
      askAdmin: "Please ask an administrator.",
      updatePayment: "Update payment",
    },
  },
  suspended: {
    title: "{{org}} is suspended",
    text: "Kontaktfix suspended this organization. Contacts already in Outlook stay, but nothing is updated. Please contact Kontaktfix.",
  },
  ops: opsEn,
  partner: partnerEn,
  errors: {
    generic: "Something went wrong. Please try again.",
    forbidden: "You do not have permission for this.",
    admin_only: "Only administrators can do this.",
    not_found: "Not found.",
    list_name_taken: "A list with this name already exists.",
    already_assigned: "Already assigned.",
    already_member: "This person is already a member.",
    last_admin: "The organization needs at least one administrator.",
    list_required: "Choose at least one of your lists.",
    not_editor_of_contact: "You can only edit contacts in your lists.",
    not_editor_of_list: "You are not an editor of this list.",
    invalid_image: "This image cannot be read.",
    empty_file: "The file contains no data.",
    unsupported_format: "Use .xlsx, .csv or .vcf.",
    recipient_limit:
      "This would give {{people}} people up to {{contacts}} contacts each; the limit is {{limit}} per person. Make the lists smaller or assign them to fewer people.",
    organization_contact_limit:
      "Your organization would then have {{contacts}} contacts; the limit is {{limit}}. Contact us for a higher limit.",
    directory_list: "This list is filled from Microsoft 365. Change the people there.",
    directory_contact: "This person comes from Microsoft 365. Change the details there.",
    file_list: "This list is filled from a file. Change the contacts at the source.",
    file_contact: "This contact comes from a file. Change the details at the source.",
    file_source_required: "Please enter the file address.",
    file_fetch_failed: "The file could not be read.",
    credentials_unavailable: "Credentials cannot be stored on this server.",
    selection_changed:
      "The contacts changed in the meantime, the selection now has {{count}}. Please check it and try again.",
    directory_source_required: "Please choose who is on the list.",
    too_many_rows:
      "The file has {{rows}} rows, the limit is {{max}}. Please split the file and import the parts one after the other.",
    organization_exists: "Your organization already exists.",
    validation_failed: "Please check the highlighted fields.",
    directory_object_not_found: "Not found in Microsoft 365.",
    not_configured: "Microsoft sign-in is not configured.",
    own_app_unavailable: "Own app registrations are not enabled on this server.",
    organization_suspended: "This organization is suspended.",
    unauthenticated: "Please sign in again.",
    invalid_credentials: "Email or password is wrong.",
    too_many_attempts: "Too many attempts. Wait 15 minutes and try again.",
    invalid_code: "The code is wrong or was already used. Wait for the next one.",
    login_expired: "Sign-in took too long. Please start again.",
    elevation_required: "Confirm with a code from your authenticator app.",
    setup_link_invalid: "This setup link no longer works.",
    weak_password: "The password must not contain your email name.",
    wrong_password: "The current password is wrong.",
    last_operator: "At least one active operator must remain.",
    admin_exists: "An operator with this email exists already.",
    not_on_self: "You cannot do this to your own account.",
    confirmation_mismatch: "The name does not match.",
    not_connected: "Microsoft 365 is not connected.",
    credentials_key_missing: "CREDENTIALS_KEY is not set on the server.",
    totp_not_started: "Scan the QR code first.",
    support_session: "Not available in a support session.",
    subscription_required: "Your free trial has ended. Subscribe under Settings → Subscription.",
    billing_not_configured: "Billing is not configured on this server.",
    partner_billed: "Your IT partner pays for Kontaktfix. You do not need your own subscription.",
    already_subscribed: "There is already an active subscription.",
    partner_suspended: "This partner account is suspended. Please contact Kontaktfix.",
    second_factor_required: "Set up a second factor first (My account).",
    no_customer_access: "You have no access to this customer. Ask your partner admin.",
    partner_admin_only: "Only partner admins can do this.",
    customer_admin_only: "Only the customer's own administrators can do this.",
    partner_not_found: "No partner was found with this code.",
    partner_session: "Not available in a partner session.",
    partner_admin_has_full_access: "Partner admins have access to every customer.",
    tenant_not_found: "No Microsoft 365 tenant was found for this domain.",
    already_linked: "This partner is already linked or a request is open.",
    last_partner_admin: "At least one partner admin has to remain.",
    email_taken: "This address already has a partner account.",
    cannot_remove_self: "You cannot remove yourself.",
    no_customer: "There are no invoices yet.",
  },
};

type Widen<T> = T extends string
  ? string
  : T extends readonly string[]
    ? readonly string[]
    : { [K in keyof T]: Widen<T[K]> };
export type Messages = Widen<typeof en>;
